import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";

import PropTypes from "prop-types";
import Modal from "../../../../parts/modal-new";
import NutrientsChart from "../../../athlete-portal/nutrition/components/NutrientsChart";
import NutrientsInputs from "../../../athlete-portal/nutrition/components/NutrientsInputs";
import KcalInputs from "../../../athlete-portal/nutrition/components/KcalInputs";
import ExtraPreferences from "../../../athlete-portal/nutrition/components/ExtraPreferences";
import AIStatusPill from "../../../../parts/ai-status-pill";
import ReportionWeekSelector from "../../../athlete-portal/nutrition/components/ReportionWeekSelector";

const ModifyWithAIPlanModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const [isLoading, setIsLoading] = useState(false);

  const mealPlan = locals?.mealPlan || {};
  const weeks = Array.isArray(mealPlan.weeks) ? mealPlan.weeks : [];
  const weekParam = Number(locals?.query?.week || 1);
  const targetIndex = isNaN(weekParam)
    ? 0
    : Math.max(0, Math.min(weeks.length - 1, weekParam - 1));
  const targetWeek = weeks[targetIndex] || {};
  const reportionStatus = targetWeek?.reportion?.status || "idle";
  const reportionError = targetWeek?.reportion?.error || null;
  const isPending = String(reportionStatus).toLowerCase() === "pending";

  const computeInitialFormFromMealPlan = () => {
    return {
      startingVar: "kcal",
      calories: 0,
      proteins: 0,
      carbs: 0,
      fats: 0,
      fibers: 0,
      proteinsPercent: 0,
      carbsPercent: 0,
      fatsPercent: 0,
      extraPreferences: "",
      selectedPresets: [],
      selectedProviders: [],
      selectedLLMs: [],
      providerWeekAssignments: {},
      parallelTasksAmount: 1,
    };
  };

  const [form, setForm] = useState(() => computeInitialFormFromMealPlan());

  const presetPrompts = [
    "Adjust meals to 1800 kcal per day",
    "Increase daily calories to 2500",
    "Reduce total calories to 1500",
    "Reduce fat intake",
    "Adjust to 50% carbs, 25% protein, 25% fat",
    "Balanced macros for maintenance",
  ];

  const aiProviders = [
    { value: "gemini", label: "Gemini" },
    { value: "openai", label: "OpenAI" },
    { value: "claude", label: "Claude" },
  ];

  const llmData = [
    {
      value: "gemini-2.5-pro",
      label: "Gemini 2.5 Pro",
      provider: "gemini",
    },
    {
      value: "gemini-2.5-flash",
      label: "Gemini 2.5 Flash",
      provider: "gemini",
    },
    {
      value: "gemini-2.5-flash-lite",
      label: "Gemini 2.5 Flash Lite",
      provider: "gemini",
    },
    {
      value: "gpt-5-2025-08-07",
      label: "GPT 5",
      provider: "openai",
    },
    {
      value: "gpt-5-mini-2025-08-07",
      label: "GPT 5 Mini",
      provider: "openai",
    },
    {
      value: "gpt-5-nano-2025-08-07",
      label: "GPT 5 Nano",
      provider: "openai",
    },
    {
      value: "claude-sonnet-4-5-20250929",
      label: "Claude 4.5 Sonnet",
      provider: "claude",
    },
    {
      value: "claude-haiku-4-5-20251001",
      label: "Claude 4.5 Haiku",
      provider: "claude",
    },
    {
      value: "claude-opus-4-1-20250805",
      label: "Claude 4.1 Opus ",
      provider: "claude",
    },
    {
      value: "claude-opus-4-20250514",
      label: "Claude 4 Opus",
      provider: "claude",
    },
    {
      value: "claude-sonnet-4-20250514",
      label: "Claude 4 Sonnet",
      provider: "claude",
    },
  ];

  const llmOptions = {};
  const llmToProviderMap = {};
  const llmTemperatureMap = {};

  for (const item of llmData) {
    if (!llmOptions[item.provider]) {
      llmOptions[item.provider] = [];
    }
    llmOptions[item.provider].push({ value: item.value, label: item.label });
    llmToProviderMap[item.value] = item.provider;
    llmTemperatureMap[item.value] =
      typeof item.temperature === "number" ? item.temperature : 1;
  }

  const handleChange = (e, updates = null) => {
    e.persist();
    const { name, value } = e.target;
    const next = Object.assign({}, form, updates ? updates : { [name]: value });
    setForm(next);
  };

  const handleStartingVar = (value) => {
    setForm((prev) => ({ ...prev, startingVar: value }));
  };

  const handlePresetPrompt = (prompt) => {
    setForm((prev) => {
      const isSelected = prev.selectedPresets.includes(prompt);
      const updatedPresets = isSelected
        ? prev.selectedPresets.filter((p) => p !== prompt)
        : [...prev.selectedPresets, prompt];
      return { ...prev, selectedPresets: updatedPresets };
    });
  };

  const removeSelectedPreset = (prompt) => {
    setForm((prev) => ({
      ...prev,
      selectedPresets: prev.selectedPresets.filter((p) => p !== prompt),
    }));
  };

  const handleparallelTasksAmountChange = (e, updates = null) => {
    e.persist();
    const { name, value } = e.target;

    const numValue = Number(value);
    const validValue = Math.min(Math.max(0, numValue), 20);
    const next = Object.assign(
      {},
      form,
      updates ? updates : { [name]: validValue }
    );
    setForm(next);
  };

  const handleProviderSelection = (provider) => {
    setForm((prev) => {
      const isSelected = prev.selectedProviders.includes(provider);
      const updatedProviders = isSelected
        ? prev.selectedProviders.filter((p) => p !== provider)
        : [...prev.selectedProviders, provider];

      const updatedLLMs = isSelected
        ? prev.selectedLLMs.filter((llm) => !llm.startsWith(provider))
        : prev.selectedLLMs;

      const updatedAssignments = { ...prev.providerWeekAssignments };
      if (isSelected) {
        Object.keys(updatedAssignments).forEach((key) => {
          if (key.startsWith(provider)) {
            delete updatedAssignments[key];
          }
        });
      }

      return {
        ...prev,
        selectedProviders: updatedProviders,
        selectedLLMs: updatedLLMs,
        providerWeekAssignments: updatedAssignments,
      };
    });
  };

  const handleLLMSelection = (llm) => {
    setForm((prev) => {
      const isSelected = prev.selectedLLMs.includes(llm);
      const updatedLLMs = isSelected
        ? prev.selectedLLMs.filter((l) => l !== llm)
        : [...prev.selectedLLMs, llm];

      const updatedAssignments = { ...prev.providerWeekAssignments };
      if (isSelected) {
        delete updatedAssignments[llm];
      }

      return {
        ...prev,
        selectedLLMs: updatedLLMs,
        providerWeekAssignments: updatedAssignments,
      };
    });
  };

  const handleWeekAssignment = (llm, week) => {
    const maxWeek = weeks.length;
    const validWeek = Math.min(Math.max(1, week), maxWeek);

    setForm((prev) => ({
      ...prev,
      providerWeekAssignments: {
        ...prev.providerWeekAssignments,
        [llm]: validWeek,
      },
    }));
  };

  const {
    startingVar,
    calories,
    proteins,
    carbs,
    fats,
    fibers,
    proteinsPercent,
    carbsPercent,
    fatsPercent,
    extraPreferences,
    selectedPresets,
    selectedProviders,
    selectedLLMs,
    providerWeekAssignments,
    parallelTasksAmount,
  } = form;

  const nutrientsMode = startingVar === "nutrients";
  const bulkMode = startingVar === "bulk";

  const handleModifyPlan = () => {
    if (isPending) return;
    setIsLoading(true);

    const { mealPlan: mealPlanId = "", week = 1 } = locals?.query || {};

    if (bulkMode) {
      const combinedPreferences = [extraPreferences?.trim(), ...selectedPresets]
        .filter(Boolean)
        .join(". ");

      const aiAssignments = selectedLLMs.map((llm) => ({
        provider: llmToProviderMap[llm],
        llm,
        temperature: llmTemperatureMap[llm],
        week: providerWeekAssignments[llm] - 1 || 0,
      }));

      const body = {
        mealPlanId,
        targetCalories: Number(calories || 0),
        targetMacros: {
          proteinPercentage: Number(proteinsPercent || 0),
          carbsPercentage: Number(carbsPercent || 0),
          fatsPercentage: Number(fatsPercent || 0),
          protein: Number(proteins || 0),
          carbs: Number(carbs || 0),
          fats: Number(fats || 0),
          fibers: Number(fibers || 0),
        },
        extraPreferences: combinedPreferences,
        aiAssignments,
        parallelTasksAmount: Number(parallelTasksAmount || 1),
        weeksLength: weeks.length,
      };

      submit(`/actions/meal-planner/bulk-reportion-meal-plan`, {
        method: "POST",
        body,
      });
    } else {
      const combinedPreferences = [extraPreferences?.trim(), ...selectedPresets]
        .filter(Boolean)
        .join(". ");

      const body = {
        mealPlanId,
        week: Number(week) - 1,
        targetCalories: Number(calories || 0),
        targetMacros: {
          proteinPercentage: Number(proteinsPercent || 0),
          carbsPercentage: Number(carbsPercent || 0),
          fatsPercentage: Number(fatsPercent || 0),
          protein: Number(proteins || 0),
          carbs: Number(carbs || 0),
          fats: Number(fats || 0),
          fibers: Number(fibers || 0),
        },
        extraPreferences: combinedPreferences,
      };

      submit(`/actions/meal-planner/reportion-meal-plan`, {
        method: "POST",
        body,
      });
    }
  };

  return (
    <Modal className="modify-with-ai-modal" title={t("editMealPlan.modifyWithAIPlanModal.title")}>
      <div className="body Create-Nutrition-Plan">
        <div className="flex items-center justify-between mb-2">
          <p className="m-0">
            {t("editMealPlan.modifyWithAIPlanModal.description")}
          </p>
          <AIStatusPill status={reportionStatus} error={reportionError} />
        </div>

        <div className="nutrition-plan-notes-row">
          <div className="form-col w-1/3">
            <label className="label">{t("editMealPlan.modifyWithAIPlanModal.startingVariable")}</label>
            <div className="starting-var-options">
              <label className="starting-var-option">
                <input
                  type="radio"
                  name="startingVar"
                  value="kcal"
                  checked={startingVar === "kcal"}
                  onChange={(e) => handleStartingVar(e.target.value)}
                />
                <span>{t("editMealPlan.modifyWithAIPlanModal.kcalTarget")}</span>
              </label>
              <label className="starting-var-option">
                <input
                  type="radio"
                  name="startingVar"
                  value="nutrients"
                  checked={startingVar === "nutrients"}
                  onChange={(e) => handleStartingVar(e.target.value)}
                />
                <span>{t("editMealPlan.modifyWithAIPlanModal.macronutrients")}</span>
              </label>
            </div>
          </div>
        </div>

        {!bulkMode && (
          <div className="nutrition-plan-macros-container">
            <div className="nutrition-plan-day-title">
              <p>{t("editMealPlan.modifyWithAIPlanModal.trainingDay")}</p>
            </div>

            <div className="flex flex-row justify-between items-center px-4 pb-4">
              <div className="w-[50%]">
                {nutrientsMode ? (
                  <NutrientsInputs
                    handleChange={handleChange}
                    proteins={proteins}
                    carbs={carbs}
                    fats={fats}
                    fibers={fibers}
                  />
                ) : (
                  <KcalInputs
                    calories={calories}
                    proteins={proteins}
                    proteinsPercent={proteinsPercent}
                    carbs={carbs}
                    carbsPercent={carbsPercent}
                    fats={fats}
                    fibers={fibers}
                    fatsPercent={fatsPercent}
                    handleChange={handleChange}
                  />
                )}
              </div>

              <div className="w-[50%]">
                <NutrientsChart
                  providedId={"nutrient-chart-modify-with-ai"}
                  dayStats={{ nutrients: { proteins, carbs, fats } }}
                  width={250}
                />
              </div>
            </div>
          </div>
        )}

        {/* {bulkMode && (
          <div className="bulk-reportioning-container">
            <div className="nutrition-plan-macros-container">
              <div className="nutrition-plan-day-title">
                <p>Bulk Repotioning Configuration</p>
              </div>

              <div className="flex flex-row justify-between items-center mt-4 p-4">
                <div className="w-[50%]">
                  <KcalInputs
                    calories={calories}
                    proteins={proteins}
                    proteinsPercent={proteinsPercent}
                    carbs={carbs}
                    carbsPercent={carbsPercent}
                    fats={fats}
                    fibers={fibers}
                    fatsPercent={fatsPercent}
                    handleChange={handleChange}
                  />
                </div>

                <div className="w-[50%]">
                  <NutrientsChart
                    providedId={"nutrient-chart-bulk-reportioning"}
                    dayStats={{
                      nutrients: {
                        proteins,
                        carbs,
                        fats,
                      },
                    }}
                    width={250}
                  />
                </div>
              </div>
            </div>

            <div className="ai-providers-section mt-6">
              <h4>AI Providers & LLMs</h4>
              <div className="providers-grid grid grid-cols-3 gap-4 mb-4">
                {aiProviders.map((provider) => (
                  <div key={provider.value} className="provider-card">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedProviders.includes(provider.value)}
                        onChange={() => handleProviderSelection(provider.value)}
                        className="mr-2"
                      />
                      <p>{provider.label}</p>
                    </label>

                    {selectedProviders.includes(provider.value) && (
                      <div className="llms-section">
                        <label className="text-sm font-medium block">
                          Available LLMs:
                        </label>
                        {llmOptions[provider.value]?.map((llm) => (
                          <div key={llm.value} className="llm-item">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedLLMs.includes(llm.value)}
                                onChange={() => handleLLMSelection(llm.value)}
                                className="mr-2"
                              />
                              {llm.label}
                            </label>

                            {selectedLLMs.includes(llm.value) && (
                              <div className="week-assignment">
                                <label className="text-xs text-gray-600">
                                  Week Assignment:
                                </label>
                                <ReportionWeekSelector
                                  value={
                                    providerWeekAssignments[llm.value] || 1
                                  }
                                  onChange={(week) =>
                                    handleWeekAssignment(llm.value, week)
                                  }
                                  weeks={weeks}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="parallel-repeat-section">
              <label className="block text-sm font-medium mb-2">
                Parallel Repeat Times (0-20):
              </label>
              <input
                type="numeric"
                name="parallelTasksAmount"
                value={parallelTasksAmount}
                onChange={handleparallelTasksAmountChange}
                min="0"
                max="20"
              />
            </div>
          </div>
        )} */}

        {/* <ExtraPreferences
          extraPreferences={extraPreferences}
          selectedPresets={selectedPresets}
          presetPrompts={presetPrompts}
          handleChange={handleChange}
          removeSelectedPreset={removeSelectedPreset}
          handlePresetPrompt={handlePresetPrompt}
        /> */}
      </div>

      <footer>
        <a
          role="button"
          data-button-type="secondary"
          data-action="cancel"
          href="/actions/refresh?modal=&sourceOwner=&week="
        >
          {t("editMealPlan.modifyWithAIPlanModal.cancel")}
        </a>
        <button
          type="button"
          data-button-type="primary"
          onClick={handleModifyPlan}
          disabled={isLoading || isPending}
          title={isPending ? t("editMealPlan.modifyWithAIPlanModal.reportioningInProgress") : undefined}
        >
          {isPending
            ? t("editMealPlan.modifyWithAIPlanModal.reportioning")
            : isLoading
            ? t("editMealPlan.modifyWithAIPlanModal.modifying")
            : t("editMealPlan.modifyWithAIPlanModal.modifyPlan")}
        </button>
      </footer>
    </Modal>
  );
};

ModifyWithAIPlanModal.propTypes = {
  locals: PropTypes.object,
};

export default ModifyWithAIPlanModal;
